import React from "react";
import { Container, Box, Typography, Divider } from "@mui/material";
import { motion } from "framer-motion";
import { useLanguage } from "../../contexts/LanguageContext";

const MotionBox = motion(Box);

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const UeberUnsPage: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const isAr = currentLanguage === "ar";

  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh" }}>
      {/* Hero */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0a0a1a 0%, #1737bc 100%)",
          py: { xs: 10, md: 14 },
          px: 3,
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="overline"
            sx={{
              color: "rgba(255,255,255,0.5)",
              letterSpacing: "4px",
              fontSize: "0.7rem",
              display: "block",
              mb: 2,
            }}
          >
            {isAr ? "نبذة عنا" : "Über uns"}
          </Typography>
          <Typography
            variant="h1"
            sx={{
              color: "white",
              fontWeight: 800,
              fontSize: { xs: "2.2rem", md: "3.5rem" },
              letterSpacing: "-1.5px",
              lineHeight: 1.1,
              mb: 3,
            }}
          >
            {isAr
              ? "خبرة. متعددة اللغات. شخصي."
              : "Erfahren. Mehrsprachig. Persönlich."}
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.65)",
              fontSize: "1.1rem",
              maxWidth: 560,
              mx: "auto",
              lineHeight: 1.8,
            }}
          >
            {isAr
              ? "TOP Buchhaltungsservice UG – شريككم الموثوق في المحاسبة والإدارة المالية."
              : "TOP Buchhaltungsservice UG – Ihr zuverlässiger Partner für Buchhaltung und Finanzmanagement."}
          </Typography>
        </Container>
      </Box>

      {/* Main content */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        {/* Intro */}
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 6, md: 10 },
            alignItems: { md: "center" },
            mb: { xs: 8, md: 12 },
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="overline"
              sx={{ color: "#1737bc", letterSpacing: "3px", fontWeight: 700, display: "block", mb: 2 }}
            >
              {isAr ? "من نحن" : "Wer wir sind"}
            </Typography>
            <Typography
              variant="h2"
              sx={{ fontWeight: 800, fontSize: { xs: "1.8rem", md: "2.4rem" }, color: "#0a0a1a", mb: 3, lineHeight: 1.2 }}
            >
              {isAr ? "TOP Buchhaltungsservice UG" : "TOP Buchhaltungsservice UG"}
            </Typography>
            <Typography sx={{ color: "#444", fontSize: "1rem", lineHeight: 1.9, mb: 2 }}>
              {isAr
                ? "TOP Buchhaltungsservice UG هي شركة متخصصة في المحاسبة المالية ومحاسبة الرواتب واستشارات الأعمال. نقدم خدماتنا للشركات الصغيرة والمتوسطة في جميع أنحاء ألمانيا."
                : "TOP Buchhaltungsservice UG ist ein auf Buchhaltung und Finanzdienstleistungen spezialisiertes Unternehmen mit Sitz in Dortmund. Wir betreuen kleine und mittelständische Unternehmen aus allen Branchen in ganz Deutschland."}
            </Typography>
            <Typography sx={{ color: "#444", fontSize: "1rem", lineHeight: 1.9 }}>
              {isAr
                ? "فريقنا يتكون من مستشار ضريبي متمرس وموظفين ضريبيين ذوي خبرة طويلة، يقدمون لكم دعمًا موثوقًا واحترافيًا باللغات الألمانية والفارسية والإنجليزية والعربية."
                : "Unser Team besteht aus einem kompetenten Steuerberater und langjährig berufserfahrenen Steuerfachangestellten, die Sie zuverlässig und professionell auf Deutsch, Persisch, Englisch und Arabisch unterstützen."}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
              minHeight: { xs: 260, md: 380 },
              backgroundImage:
                "url(https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&q=80)",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        </MotionBox>

        <Divider sx={{ mb: { xs: 8, md: 12 } }} />

        {/* Values */}
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          sx={{ mb: { xs: 8, md: 12 } }}
        >
          <Typography
            variant="overline"
            sx={{ color: "#1737bc", letterSpacing: "3px", fontWeight: 700, display: "block", mb: 2, textAlign: "center" }}
          >
            {isAr ? "قيمنا" : "Unsere Werte"}
          </Typography>
          <Typography
            variant="h2"
            sx={{ fontWeight: 800, fontSize: { xs: "1.8rem", md: "2.4rem" }, color: "#0a0a1a", mb: 8, textAlign: "center", lineHeight: 1.2 }}
          >
            {isAr ? "ما يميزنا" : "Was uns auszeichnet"}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
              gap: 4,
            }}
          >
            {[
              {
                image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&q=80",
                title: isAr ? "الدقة" : "Präzision",
                desc: isAr
                  ? "نعمل بأعلى مستويات الدقة والاحترافية في جميع المهام المحاسبية."
                  : "Alle buchhalterischen Aufgaben werden mit höchster Genauigkeit und Sorgfalt erledigt.",
              },
              {
                image: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=600&q=80",
                title: isAr ? "التعدد اللغوي" : "Mehrsprachigkeit",
                desc: isAr
                  ? "نقدم خدماتنا بأربع لغات: الألمانية والفارسية والإنجليزية والعربية."
                  : "Wir beraten Sie auf Deutsch, Persisch, Englisch und Arabisch.",
              },
              {
                image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&q=80",
                title: isAr ? "الشخصية" : "Persönlichkeit",
                desc: isAr
                  ? "أنتم تحصلون على مسؤول شخصي متاح دائمًا لأسئلتكم."
                  : "Sie erhalten einen festen Ansprechpartner, der immer für Ihre Fragen da ist.",
              },
              {
                image: "https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=600&q=80",
                title: isAr ? "الالتزام بالمواعيد" : "Termintreue",
                desc: isAr
                  ? "نلتزم دائمًا بالمواعيد ونسلم الأعمال في الوقت المحدد."
                  : "Wir halten Fristen zuverlässig ein – pünktlich und verlässlich.",
              },
            ].map((v) => (
              <Box
                key={v.title}
                sx={{
                  bgcolor: "#fafafa",
                  borderRadius: "12px",
                  border: "1px solid #f0f0f5",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: 180,
                    backgroundImage: `url(${v.image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <Box sx={{ p: 3 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#0a0a1a", mb: 1.5 }}>
                    {v.title}
                  </Typography>
                  <Typography sx={{ color: "#666", fontSize: "0.9rem", lineHeight: 1.7 }}>
                    {v.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </MotionBox>

        <Divider sx={{ mb: { xs: 8, md: 12 } }} />

        {/* Contact CTA */}
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          sx={{
            textAlign: "center",
            background: "linear-gradient(135deg, #0a0a1a 0%, #1737bc 100%)",
            borderRadius: "20px",
            p: { xs: 6, md: 10 },
          }}
        >
          <Typography
            variant="h2"
            sx={{ color: "white", fontWeight: 800, fontSize: { xs: "1.8rem", md: "2.4rem" }, mb: 2 }}
          >
            {isAr ? "هل لديكم أسئلة؟" : "Haben Sie Fragen?"}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "1rem", mb: 4 }}>
            {isAr
              ? "تواصلوا معنا – نحن هنا لمساعدتكم."
              : "Sprechen Sie uns an – wir sind für Sie da."}
          </Typography>
          <Box
            component="a"
            href="/contact"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              bgcolor: "white",
              color: "#0a0a1a",
              px: 4,
              py: 1.6,
              borderRadius: "8px",
              fontWeight: 700,
              fontSize: "0.95rem",
              textDecoration: "none",
              "&:hover": { bgcolor: "#f0f0f0" },
              transition: "background 0.2s",
            }}
          >
            {isAr ? "تواصل معنا" : "Kontakt aufnehmen"}
          </Box>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default UeberUnsPage;
